import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { RefreshCw, LogOut, Download, Mail, Phone, MapPin, KeyRound, UserPlus, CheckCircle2, Trash2 } from "lucide-react";
import CustomersPanel, { CustomerDialog } from "@/components/admin/CustomersPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "om_admin_token";

export default function AdminLeads() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [input, setInput] = useState("");
  const [leads, setLeads] = useState([]);
  const [o2Interests, setO2Interests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("leads");
  const [convertPrefill, setConvertPrefill] = useState(null);

  const load = useCallback(async (tok) => {
    setLoading(true);
    try {
      const headers = { "X-Admin-Token": tok };
      const [leadsRes, statsRes, o2Res] = await Promise.all([
        axios.get(`${API}/admin/leads?limit=500`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/overlander-2-interests?limit=500`, { headers }),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
      setO2Interests(o2Res.data);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 401) {
        toast.error("Invalid admin token.");
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        toast.error("Could not load leads. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const submitToken = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    localStorage.setItem(TOKEN_KEY, input.trim());
    setToken(input.trim());
  };

  const deleteLead = async (lead, opts = {}) => {
    if (!opts.skipConfirm && !window.confirm(`Delete trial request from ${lead.name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/leads/${lead.id}`, {
        headers: { "X-Admin-Token": token },
      });
      toast.success("Trial request deleted.");
      setSelected(null);
      load(token);
    } catch {
      toast.error("Could not delete trial request.");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setLeads([]);
    setO2Interests([]);
    setStats(null);
  };

  const deleteO2Interest = async (interest) => {
    if (!window.confirm(`Delete Overlander 2 interest from ${interest.name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/overlander-2-interests/${interest.id}`, {
        headers: { "X-Admin-Token": token },
      });
      toast.success("Overlander 2 interest deleted.");
      load(token);
    } catch {
      toast.error("Could not delete interest.");
    }
  };

  const exportCsv = () => {
    if (!leads.length) return;
    const headers = ["created_at", "name", "email", "phone", "state", "terrain", "message"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [headers.join(",")].concat(
      leads.map((l) => headers.map((h) => esc(l[h])).join(","))
    );
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `om-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <form
          onSubmit={submitToken}
          data-testid="admin-login-form"
          className="w-full max-w-sm bg-card border border-border rounded-sm p-8"
        >
          <h1 className="font-display text-2xl font-bold uppercase tracking-tight">
            Overlander <span className="text-primary">Admin</span>
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Enter your admin token to view captured trial leads.
          </p>
          <div className="mt-6 space-y-2">
            <Label className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
              Admin token
            </Label>
            <Input
              data-testid="admin-token-input"
              type="password"
              autoFocus
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="rounded-sm"
              placeholder="••••••••"
            />
          </div>
          <Button
            data-testid="admin-login-btn"
            type="submit"
            className="mt-6 w-full rounded-sm uppercase tracking-wider font-bold py-6 text-sm"
          >
            Unlock
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/60 backdrop-blur">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between">
          <h1 className="font-display font-bold uppercase tracking-tight">
            Overlander <span className="text-primary">Admin</span>
          </h1>
          <div className="flex items-center gap-2">
            <ChangeTokenDialog token={token} onChanged={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); }} />
            <Button
              data-testid="admin-refresh-btn"
              size="sm"
              variant="outline"
              onClick={() => load(token)}
              disabled={loading}
              className="rounded-sm"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              data-testid="admin-export-btn"
              size="sm"
              variant="outline"
              onClick={exportCsv}
              disabled={!leads.length}
              className="rounded-sm"
            >
              <Download className="h-4 w-4 mr-2" /> CSV
            </Button>
            <Button
              data-testid="admin-logout-btn"
              size="sm"
              variant="ghost"
              onClick={logout}
              className="rounded-sm"
            >
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 lg:px-10 py-10 space-y-8">
        {stats && (
          <section data-testid="admin-stats" className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard label="Total leads" value={stats.total} highlight />
            <StatCard label="Converted" value={stats.converted ?? 0} />
            <StatCard label="Customers" value={stats.customers_total ?? 0} />
            <StatCard label="VIC leads" value={stats.by_state?.VIC ?? 0} />
            <StatCard label="Other states" value={(stats.total || 0) - (stats.by_state?.VIC ?? 0)} />
          </section>
        )}

        <div className="flex items-center gap-1 border-b border-border" data-testid="admin-tabs">
          <TabButton id="leads" active={tab === "leads"} onClick={() => setTab("leads")}>
            Trial Requests
            <span className="ml-2 text-xs text-muted-foreground">{leads.length}</span>
          </TabButton>
          <TabButton id="o2" active={tab === "o2"} onClick={() => setTab("o2")}>
            Overlander 2
            <span className="ml-2 text-xs text-muted-foreground">{o2Interests.length}</span>
          </TabButton>
          <TabButton id="customers" active={tab === "customers"} onClick={() => setTab("customers")}>
            CRM · Customers
            <span className="ml-2 text-xs text-muted-foreground">{stats?.customers_total ?? 0}</span>
          </TabButton>
        </div>

        {tab === "leads" && (
        <section>
          <div className="bg-card border border-border rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-leads-table">
                <thead className="bg-muted/60">
                  <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                    <th className="px-4 py-3 font-semibold">Received</th>
                    <th className="px-4 py-3 font-semibold">Name</th>
                    <th className="px-4 py-3 font-semibold">Contact</th>
                    <th className="px-4 py-3 font-semibold">State</th>
                    <th className="px-4 py-3 font-semibold">Terrain</th>
                    <th className="px-4 py-3 font-semibold">Message</th>
                    <th className="px-4 py-3 font-semibold text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.length === 0 && !loading && (
                    <tr>
                      <td colSpan={7} className="px-4 py-16 text-center text-muted-foreground">
                        No trial requests captured yet.
                      </td>
                    </tr>
                  )}
                  {leads.map((l) => (
                    <tr
                      key={l.id}
                      data-testid={`admin-lead-${l.id}`}
                      onClick={() => setSelected(l)}
                      className="border-t border-border hover:bg-muted/40 transition-colors cursor-pointer"
                    >
                      <td className="px-4 py-3 align-top whitespace-nowrap text-muted-foreground">
                        {formatDate(l.created_at)}
                      </td>
                      <td className="px-4 py-3 align-top font-medium">
                        {l.name}
                        {l.converted_customer_id && (
                          <div className="mt-1">
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm bg-green-600/15 text-green-700 border border-green-600/30">
                              <CheckCircle2 className="h-3 w-3" /> Converted
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top" onClick={(e) => e.stopPropagation()}>
                        <a href={`mailto:${l.email}`} className="flex items-center gap-1.5 text-primary hover:underline">
                          <Mail className="h-3 w-3" /> {l.email}
                        </a>
                        <a href={`tel:${l.phone}`} className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground mt-1">
                          <Phone className="h-3 w-3" /> {l.phone}
                        </a>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider bg-primary/15 text-primary px-2 py-1 rounded-sm">
                          <MapPin className="h-3 w-3" /> {l.state}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-top text-muted-foreground">
                        {l.terrain || "—"}
                      </td>
                      <td className="px-4 py-3 align-top text-muted-foreground max-w-sm">
                        <div className="line-clamp-3 whitespace-pre-wrap">{l.message || "—"}</div>
                      </td>
                      <td className="px-4 py-3 align-top text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="inline-flex items-center gap-1.5 justify-end">
                          {l.converted_customer_id ? (
                            <span className="text-xs text-muted-foreground">In CRM</span>
                          ) : (
                            <Button
                              data-testid={`add-to-crm-${l.id}`}
                              size="sm"
                              variant="outline"
                              className="rounded-sm"
                              onClick={() => setConvertPrefill({
                                name: l.name,
                                email: l.email,
                                phone: l.phone,
                                state: l.state,
                                notes: l.message ? `From trial request:\n${l.message}` : "",
                                status: "new",
                                source_lead_id: l.id,
                              })}
                            >
                              <UserPlus className="h-4 w-4 mr-1.5" /> Add to CRM
                            </Button>
                          )}
                          <Button
                            data-testid={`delete-lead-${l.id}`}
                            size="sm"
                            variant="ghost"
                            className="rounded-sm text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => deleteLead(l)}
                            aria-label="Delete trial request"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
        )}

        {tab === "o2" && (
          <section>
            <div className="bg-card border border-border rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-o2-table">
                  <thead className="bg-muted/60">
                    <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                      <th className="px-4 py-3 font-semibold">Received</th>
                      <th className="px-4 py-3 font-semibold">Name</th>
                      <th className="px-4 py-3 font-semibold">Contact</th>
                      <th className="px-4 py-3 font-semibold">Interest</th>
                      <th className="px-4 py-3 font-semibold text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {o2Interests.length === 0 && !loading && (
                      <tr>
                        <td colSpan={5} className="px-4 py-16 text-center text-muted-foreground">
                          No Overlander 2 interest registered yet.
                        </td>
                      </tr>
                    )}
                    {o2Interests.map((it) => (
                      <tr
                        key={it.id}
                        data-testid={`admin-o2-${it.id}`}
                        className="border-t border-border hover:bg-muted/40 transition-colors"
                      >
                        <td className="px-4 py-3 align-top whitespace-nowrap text-muted-foreground">
                          {formatDate(it.created_at)}
                        </td>
                        <td className="px-4 py-3 align-top font-medium">{it.name}</td>
                        <td className="px-4 py-3 align-top">
                          <a href={`mailto:${it.email}`} className="flex items-center gap-1.5 text-primary hover:underline">
                            <Mail className="h-3 w-3" /> {it.email}
                          </a>
                          <a href={`tel:${it.phone}`} className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground mt-1">
                            <Phone className="h-3 w-3" /> {it.phone}
                          </a>
                        </td>
                        <td className="px-4 py-3 align-top">
                          {it.request_quote ? (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-sm bg-primary text-primary-foreground">
                              Quote requested
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-sm bg-muted text-muted-foreground border border-border">
                              Waitlist
                            </span>
                          )}
                          {it.converted_customer_id && (
                            <span className="mt-1.5 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-sm bg-green-600/15 text-green-700 border border-green-600/40">
                              <CheckCircle2 className="h-3 w-3" /> In CRM
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 align-top text-right">
                          <div className="inline-flex items-center gap-1.5 justify-end">
                            {!it.converted_customer_id && (
                              <Button
                                data-testid={`add-o2-to-crm-${it.id}`}
                                size="sm"
                                variant="outline"
                                className="rounded-sm"
                                onClick={() => setConvertPrefill({
                                  name: it.name,
                                  email: it.email,
                                  phone: it.phone,
                                  state: "",
                                  notes: it.request_quote
                                    ? "From Overlander 2 interest · quote requested."
                                    : "From Overlander 2 interest · waitlist.",
                                  status: "new",
                                  source_o2_interest_id: it.id,
                                })}
                              >
                                <UserPlus className="h-4 w-4 mr-1.5" /> Add to CRM
                              </Button>
                            )}
                            <Button
                              asChild
                              data-testid={`o2-email-${it.id}`}
                              size="sm"
                              variant="outline"
                              className="rounded-sm"
                            >
                              <a href={`mailto:${it.email}?subject=${encodeURIComponent("Your Overlander 2 interest")}`}>
                                <Mail className="h-4 w-4 mr-1.5" /> Reply
                              </a>
                            </Button>
                            <Button
                              data-testid={`delete-o2-${it.id}`}
                              size="sm"
                              variant="ghost"
                              className="rounded-sm text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => deleteO2Interest(it)}
                              aria-label="Delete Overlander 2 interest"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {tab === "customers" && <CustomersPanel token={token} />}
      </main>

      <LeadDetailDialog lead={selected} onClose={() => setSelected(null)} onDelete={deleteLead} />
      <CustomerDialog
        open={!!convertPrefill}
        onClose={() => setConvertPrefill(null)}
        token={token}
        prefill={convertPrefill}
        onSaved={() => { setConvertPrefill(null); setTab("customers"); load(token); }}
      />
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-3 text-sm font-bold uppercase tracking-[0.15em] border-b-2 -mb-px transition-colors ${
        active
          ? "text-primary border-primary"
          : "text-muted-foreground border-transparent hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

function StatCard({ label, value, highlight }) {
  return (
    <div
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`rounded-sm border p-5 ${
        highlight ? "border-primary bg-primary/5" : "border-border bg-card"
      }`}
    >
      <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
        {label}
      </div>
      <div className="mt-2 font-display text-3xl font-black text-foreground">{value}</div>
    </div>
  );
}

function LeadDetailDialog({ lead, onClose, onDelete }) {
  const open = !!lead;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="lead-detail-dialog" className="rounded-sm max-w-2xl">
        {lead && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display uppercase tracking-tight text-2xl">
                {lead.name}
              </DialogTitle>
              <DialogDescription>
                Received {formatDate(lead.created_at)} · Lead ID {lead.id}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-5 mt-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <DetailField label="Email">
                  <a href={`mailto:${lead.email}`} className="text-primary hover:underline break-all">
                    {lead.email}
                  </a>
                </DetailField>
                <DetailField label="Phone">
                  <a href={`tel:${lead.phone}`} className="text-primary hover:underline">
                    {lead.phone}
                  </a>
                </DetailField>
                <DetailField label="State">
                  <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider bg-primary/15 text-primary px-2 py-1 rounded-sm">
                    <MapPin className="h-3 w-3" /> {lead.state}
                  </span>
                </DetailField>
                <DetailField label="Terrain">{lead.terrain || "—"}</DetailField>
              </div>
              <DetailField label="Message">
                <div
                  data-testid="lead-detail-message"
                  className="bg-muted/60 border border-border rounded-sm p-4 text-sm leading-relaxed whitespace-pre-wrap break-words max-h-80 overflow-y-auto"
                >
                  {lead.message || <span className="text-muted-foreground italic">No message provided.</span>}
                </div>
              </DetailField>
              <div className="flex flex-wrap gap-2 pt-2 border-t border-border justify-between">
                <div className="flex flex-wrap gap-2">
                  <Button asChild size="sm" className="rounded-sm">
                    <a href={`mailto:${lead.email}?subject=${encodeURIComponent("Your Overlander 4 trial request")}`}>
                      <Mail className="h-4 w-4 mr-2" /> Reply by email
                    </a>
                  </Button>
                  <Button asChild size="sm" variant="outline" className="rounded-sm">
                    <a href={`tel:${lead.phone}`}>
                      <Phone className="h-4 w-4 mr-2" /> Call
                    </a>
                  </Button>
                </div>
                <Button
                  data-testid="lead-detail-delete-btn"
                  size="sm"
                  variant="ghost"
                  className="rounded-sm text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => onDelete && onDelete(lead)}
                >
                  <Trash2 className="h-4 w-4 mr-2" /> Delete
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DetailField({ label, children }) {
  return (
    <div className="space-y-1.5">
      <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
        {label}
      </div>
      <div className="text-sm font-medium">{children}</div>
    </div>
  );
}


function ChangeTokenDialog({ token, onChanged }) {
  const [open, setOpen] = useState(false);
  const [newToken, setNewToken] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newToken.length < 8) {
      toast.error("New token must be at least 8 characters.");
      return;
    }
    if (newToken !== confirm) {
      toast.error("New token and confirmation do not match.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/change-token`, {
        current_token: token,
        new_token: newToken,
      });
      toast.success("Admin token updated.");
      onChanged(newToken);
      setOpen(false);
      setNewToken("");
      setConfirm("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not update token.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="admin-change-token-btn"
          size="sm"
          variant="outline"
          className="rounded-sm"
        >
          <KeyRound className="h-4 w-4 mr-2" /> Change token
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="change-token-dialog" className="rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight">
            Rotate admin token
          </DialogTitle>
          <DialogDescription>
            Enter a new admin token. Minimum 8 characters. You&apos;ll stay logged in on this device;
            other devices will need the new token.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
              New token
            </Label>
            <Input
              data-testid="new-token-input"
              type="password"
              value={newToken}
              onChange={(e) => setNewToken(e.target.value)}
              autoFocus
              className="rounded-sm"
              placeholder="At least 8 characters"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
              Confirm new token
            </Label>
            <Input
              data-testid="confirm-token-input"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="rounded-sm"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button
              data-testid="submit-change-token"
              type="submit"
              disabled={busy}
              className="rounded-sm uppercase tracking-wider font-bold"
            >
              {busy ? "Updating..." : "Update token"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}
